import { FilterClauseType, type BlotterTableFilter, type FilterClause, type UseFilterBuilderProps } from '@talos/kyoko';
import type {
  DefinedSpaceParameterPanelValueSupport,
  SpaceParameterKey,
  SpaceParameterPanelValueBinding,
  SpaceParameterPanelValueBindings,
  SpaceParameterPanelValueDefinition,
  SpaceParameterPanelValueDefinitions,
  SpaceParameterValues,
} from './types';

export function defineSpaceParameterPanelValues<const TDefinitions extends SpaceParameterPanelValueDefinitions>(
  parameterToPanelValues: TDefinitions
): DefinedSpaceParameterPanelValueSupport<TDefinitions> {
  const normalizedParameterToPanelValues = Object.fromEntries(
    Object.entries(parameterToPanelValues).map(([key, definition]) => [
      key,
      normalizePanelValueBinding(definition as SpaceParameterPanelValueDefinition),
    ])
  ) as DefinedSpaceParameterPanelValueSupport<TDefinitions>['parameterToPanelValues'];

  return {
    keys: Object.keys(parameterToPanelValues) as Extract<keyof TDefinitions, string>[],
    parameterToPanelValues: normalizedParameterToPanelValues,
  };
}

export function firstSelectionToPanelValue<const TPanelValueKey extends string>(
  panelValueKey: TPanelValueKey
): SpaceParameterPanelValueBinding & { panelValueKeys: readonly [TPanelValueKey] } {
  return {
    panelValueKeys: [panelValueKey],
    parameterValueToPanelValues: value => {
      const [selection] = Array.isArray(value) ? value : [];
      return selection == null ? {} : { [panelValueKey]: selection };
    },
  };
}

export function filterClausesToSpaceParameterValues<TKey extends string>(
  clausesByKey: Map<string, FilterClause>,
  keys: readonly TKey[]
): Partial<Record<TKey, string[]>> {
  return Object.fromEntries(
    keys.flatMap(key => {
      const selections = clausesByKey.get(key)?.selections ?? [];
      return selections.length > 0 ? [[key, selections]] : [];
    })
  ) as Partial<Record<TKey, string[]>>;
}

export function spaceParameterValuesToFilterClauses<TKey extends string>(
  values: Partial<Record<TKey, unknown>>,
  keys: readonly TKey[]
): FilterClause<TKey>[] {
  return keys.flatMap(key => {
    const selections = values[key];
    return Array.isArray(selections) && selections.length > 0
      ? [
          {
            key,
            type: FilterClauseType.INCLUSIVE,
            selections,
          },
        ]
      : [];
  });
}

export function filterKeyToSpaceParameterKey(
  filterKey: string | undefined,
  supportedKeys: readonly SpaceParameterKey[],
  parameterToPanelValues: SpaceParameterPanelValueBindings
) {
  return filterKey == null
    ? undefined
    : supportedKeys.find(key => getSpaceParameterPanelValueKeys(key, parameterToPanelValues).includes(filterKey));
}

export function removeSpaceParameterKeys<TFilter extends BlotterTableFilter>(
  filter: TFilter,
  keys: readonly SpaceParameterKey[],
  parameterToPanelValues: SpaceParameterPanelValueBindings
): TFilter {
  const nextFilter = { ...filter };
  let changed = false;

  for (const key of keys) {
    for (const panelValueKey of getSpaceParameterPanelValueKeys(key, parameterToPanelValues)) {
      if (panelValueKey in nextFilter) {
        delete nextFilter[panelValueKey];
        changed = true;
      }
    }
  }

  return changed ? nextFilter : filter;
}

export function mergeSpaceParameterValues<TFilter extends BlotterTableFilter>(
  localFilter: TFilter,
  values: SpaceParameterValues,
  controlledKeys: readonly SpaceParameterKey[],
  parameterToPanelValues: SpaceParameterPanelValueBindings
): TFilter {
  if (controlledKeys.length === 0) {
    return localFilter;
  }

  let mergedFilter: BlotterTableFilter = {
    ...removeSpaceParameterKeys(localFilter, controlledKeys, parameterToPanelValues),
  };

  for (const key of controlledKeys) {
    const value = values[key];
    const binding = parameterToPanelValues[key];
    if (binding == null || value == null) {
      continue;
    }
    mergedFilter = {
      ...mergedFilter,
      ...getParameterPanelValues(binding, value, mergedFilter),
    };
  }

  return mergedFilter as TFilter;
}

export function omitControlledFilterBuilderProps(
  filterBuilderProps: UseFilterBuilderProps,
  controlledKeys: readonly SpaceParameterKey[],
  parameterToPanelValues: SpaceParameterPanelValueBindings
): UseFilterBuilderProps {
  if (controlledKeys.length === 0) {
    return filterBuilderProps;
  }

  const controlledKeySet = new Set<string>(
    controlledKeys.flatMap(key => getSpaceParameterPanelValueKeys(key, parameterToPanelValues))
  );
  return {
    ...filterBuilderProps,
    initialFilterClauses: filterBuilderProps.initialFilterClauses.filter(clause => !controlledKeySet.has(clause.key)),
    excludedPropertyKeys: [
      ...new Set([
        ...(filterBuilderProps.excludedPropertyKeys ?? []),
        ...filterBuilderProps.properties.flatMap(property =>
          controlledKeySet.has(property.key) ? [property.key] : []
        ),
      ]),
    ],
  };
}

function normalizePanelValueBinding(definition: SpaceParameterPanelValueDefinition): SpaceParameterPanelValueBinding {
  if (typeof definition === 'string') {
    return { panelValueKeys: [definition] };
  }
  if (isPanelValueBinding(definition)) {
    return definition;
  }
  return { panelValueKeys: definition };
}

function isPanelValueBinding(
  definition: SpaceParameterPanelValueDefinition
): definition is SpaceParameterPanelValueBinding {
  return typeof definition === 'object' && !Array.isArray(definition) && 'panelValueKeys' in definition;
}

function getParameterPanelValues(
  binding: SpaceParameterPanelValueBinding,
  value: unknown,
  currentPanelValues: BlotterTableFilter
): Record<string, unknown> {
  if (binding.parameterValueToPanelValues != null) {
    return binding.parameterValueToPanelValues(value, currentPanelValues);
  }
  if (!Array.isArray(value) || value.length === 0) {
    return {};
  }
  const [panelValueKey] = binding.panelValueKeys;
  return panelValueKey == null ? {} : { [panelValueKey]: value };
}

function getSpaceParameterPanelValueKeys(
  key: SpaceParameterKey,
  parameterToPanelValues: SpaceParameterPanelValueBindings
): string[] {
  return [...(parameterToPanelValues[key]?.panelValueKeys ?? [])];
}
